import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { Chart, CommonSeriesSettings, Series, Legend, Tooltip, ValueAxis, Label, ArgumentAxis } from "devextreme-react/chart";
import { controlTypes, externalApiTypes, valueTypeCodes } from "../../../../Config/constants";
import { ArrowRight, TrendingUpDown, CheckCircle2 } from "lucide-react";

export default function InsightsTab({ allMeasurements = [], graphProjects = [], currency, loading, timeRangeDates, activeProjection, serverInsights = null }) {
  const classes = useStyles();
  const currencySymbol = currency?.symbol || "€";

  // ─── Derived data ───────────────────────────────────────────────

  const computedInsights = useMemo(() => {
    if (!allMeasurements || allMeasurements.length === 0) {
      return null;
    }

    const measurements = allMeasurements;

    // Loss = anything not value-added (NVA, Waste, or unclassified non-profit)
    const isLossMeasurement = (m) => !m.isProfit && m.valueTypeId !== valueTypeCodes.VA;

    // Total value created (profit items)
    const totalValueCreated = measurements.filter((m) => m.isProfit && m.eventPrice > 0).reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Total production cost (non-profit / cost items)
    const totalProductionLoss = measurements.filter((m) => !m.isProfit && m.eventPrice > 0).reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Split production cost into value added vs no value added vs waste
    const valueAddedCost = measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0 && m.valueTypeId === valueTypeCodes.VA)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);
    const noValueAddedCost = measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0 && m.valueTypeId === valueTypeCodes.NVA)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);
    const wasteCost = measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0 && m.valueTypeId === valueTypeCodes.W)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Actual loss excludes value-added cost
    const totalActualLoss = measurements
      .filter((m) => isLossMeasurement(m) && m.eventPrice > 0)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Netto contribution
    const nettoContribution = totalValueCreated - totalProductionLoss;

    // Date range for per-day calculations — derived from first and last measurement startTime
    const measurementDates = measurements
      .map((m) => (m.startTime ? new Date(m.startTime) : null))
      .filter((d) => d && !isNaN(d));
    const rangeStart = measurementDates.length > 0 ? new Date(Math.min(...measurementDates)) : null;
    const rangeEnd = measurementDates.length > 0 ? new Date(Math.max(...measurementDates)) : null;
    const daySpan = rangeStart && rangeEnd ? Math.max(1, Math.ceil((rangeEnd - rangeStart) / 86400000) + 1) : 1;

    // ─── Loss by line (per registration point / project resource) ───
    const lossByLine = {};
    measurements
      .filter((m) => isLossMeasurement(m) && m.eventPrice > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].value += m.eventPrice || 0;
      });
    // Add duration from all loss stopwatch measurements (regardless of eventPrice)
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].duration += m.durationInMinutes;
      });

    const lossByLineData = Object.entries(lossByLine)
      .map(([name, data]) => ({
        name,
        value: data.value,
        duration: data.duration,
        lossPerWeek: daySpan > 0 ? (data.value / daySpan) * 7 : 0,
        yearlyImpact: daySpan > 0 ? (data.value / daySpan) * 365 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    // ─── Top loss drivers (top 5 descriptions / reasons) ───
    const lossByReason = {};
    measurements
      .filter((m) => isLossMeasurement(m) && m.eventPrice > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].value += m.eventPrice || 0;
      });
    // Add duration from all loss stopwatch measurements (regardless of eventPrice)
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].duration += m.durationInMinutes;
      });

    const topLossDrivers = Object.entries(lossByReason)
      .map(([reason, data]) => ({
        reason,
        value: data.value,
        duration: data.duration,
        valueTypeId: data.valueTypeId,
        percentage: totalActualLoss > 0 ? ((data.value / totalActualLoss) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => a.value - b.value)
      .slice(-5);

    // ─── Stopwatch-based stats (disruptions) ───
    const stopwatchMeasurements = measurements.filter(
      (m) => m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH
    );
    const stopwatchLossMeasurements = stopwatchMeasurements.filter((m) => isLossMeasurement(m));

    // Average loss per day
    const avgLossPerDay = totalActualLoss / daySpan;

    // Average disruptions per day
    const avgDisruptionsPerDay = stopwatchLossMeasurements.length / daySpan;

    // Average disruption duration (minutes)
    const durationsMinutes = stopwatchLossMeasurements
      .map((m) => m.durationInMinutes)
      .filter((d) => d !== null && d !== undefined && d > 0);
    const avgDisruptionDuration =
      durationsMinutes.length > 0 ? durationsMinutes.reduce((s, d) => s + d, 0) / durationsMinutes.length : 0;

    // Percentage of potential value lost (loss vs value created + actual loss)
    const totalPotentialValue = totalValueCreated + totalActualLoss;
    const lossPercentage = totalPotentialValue > 0 ? ((totalActualLoss / totalPotentialValue) * 100).toFixed(1) : 0;

    // Weekly and yearly total loss
    const weeklyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 7 : 0;
    const yearlyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 365 : 0;

    // Loss lines vs total lines
    const lossLineCount = Object.keys(lossByLine).length;
    const totalLineCount = graphProjects.length;

    // Worst performing line
    const worstLine = lossByLineData.length > 0 ? lossByLineData[0] : null;
    const secondWorstLine = lossByLineData.length > 1 ? lossByLineData[1] : null;

    return {
      totalValueCreated,
      totalProductionLoss,
      totalActualLoss,
      valueAddedCost,
      noValueAddedCost,
      wasteCost,
      nettoContribution,
      lossPercentage,
      lossByLineData,
      topLossDrivers,
      avgLossPerDay,
      avgDisruptionsPerDay,
      avgDisruptionDuration,
      rangeStart,
      rangeEnd,
      daySpan,
      weeklyTotalLoss,
      yearlyTotalLoss,
      lossLineCount,
      totalLineCount,
      worstLine,
      secondWorstLine,
    };
  }, [allMeasurements, graphProjects]);

  // Prefer the server-computed aggregate (GetGraphInsights) when provided; otherwise fall back to
  // the client-side computation above. The server DTO doesn't carry worstLine/secondWorstLine, so
  // derive them from lossByLineData exactly as the client path does. A null rangeStart means the
  // server found no measurements in range -> treat as "no insights" (same as the client's null).
  const insights = useMemo(() => {
    if (serverInsights) {
      if (!serverInsights.rangeStart) return null;
      const lossByLineData = serverInsights.lossByLineData || [];
      return {
        ...serverInsights,
        worstLine: lossByLineData.length > 0 ? lossByLineData[0] : null,
        secondWorstLine: lossByLineData.length > 1 ? lossByLineData[1] : null,
      };
    }
    return computedInsights;
  }, [serverInsights, computedInsights]);

  // ─── Projected data (only when projection is active) ────────────

  const hasProjection = !!activeProjection && allMeasurements.some((m) => m.projectedEventPrice != null);

  const projectedInsights = useMemo(() => {
    if (!hasProjection || !allMeasurements || allMeasurements.length === 0) return null;

    const measurements = allMeasurements;
    const getPrice = (m) => m.projectedEventPrice ?? m.eventPrice ?? 0;

    // Loss = anything not value-added
    const isLossMeasurement = (m) => !m.isProfit && m.valueTypeId !== valueTypeCodes.VA;

    const totalValueCreated = measurements.filter((m) => m.isProfit && getPrice(m) > 0).reduce((sum, m) => sum + getPrice(m), 0);
    const totalProductionLoss = measurements.filter((m) => !m.isProfit && getPrice(m) > 0).reduce((sum, m) => sum + getPrice(m), 0);
    const valueAddedCost = measurements
      .filter((m) => !m.isProfit && getPrice(m) > 0 && m.valueTypeId === valueTypeCodes.VA)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const noValueAddedCost = measurements
      .filter((m) => !m.isProfit && getPrice(m) > 0 && m.valueTypeId === valueTypeCodes.NVA)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const wasteCost = measurements
      .filter((m) => !m.isProfit && getPrice(m) > 0 && m.valueTypeId === valueTypeCodes.W)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const totalActualLoss = measurements
      .filter((m) => isLossMeasurement(m) && getPrice(m) > 0)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const nettoContribution = totalValueCreated - totalProductionLoss;

    const projMeasurementDates = measurements
      .map((m) => (m.startTime ? new Date(m.startTime) : null))
      .filter((d) => d && !isNaN(d));
    const rangeStart = projMeasurementDates.length > 0 ? new Date(Math.min(...projMeasurementDates)) : null;
    const rangeEnd = projMeasurementDates.length > 0 ? new Date(Math.max(...projMeasurementDates)) : null;
    const daySpan = rangeStart && rangeEnd ? Math.max(1, Math.ceil((rangeEnd - rangeStart) / 86400000) + 1) : 1;

    // Loss by line
    const lossByLine = {};
    measurements
      .filter((m) => isLossMeasurement(m) && getPrice(m) > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].value += getPrice(m);
      });
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].duration += m.durationInMinutes;
      });

    const lossByLineData = Object.entries(lossByLine)
      .map(([name, data]) => ({
        name,
        value: data.value,
        duration: data.duration,
        lossPerWeek: daySpan > 0 ? (data.value / daySpan) * 7 : 0,
        yearlyImpact: daySpan > 0 ? (data.value / daySpan) * 365 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    // Top loss drivers
    const lossByReason = {};
    measurements
      .filter((m) => isLossMeasurement(m) && getPrice(m) > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].value += getPrice(m);
      });
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].duration += m.durationInMinutes;
      });

    const topLossDrivers = Object.entries(lossByReason)
      .map(([reason, data]) => ({
        reason,
        value: data.value,
        duration: data.duration,
        valueTypeId: data.valueTypeId,
        percentage: totalActualLoss > 0 ? ((data.value / totalActualLoss) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => a.value - b.value)
      .slice(-5);

    const totalPotentialValue = totalValueCreated + totalActualLoss;
    const lossPercentage = totalPotentialValue > 0 ? ((totalActualLoss / totalPotentialValue) * 100).toFixed(1) : 0;

    const avgLossPerDay = totalActualLoss / daySpan;
    const weeklyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 7 : 0;
    const yearlyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 365 : 0;

    const lossLineCount = Object.keys(lossByLine).length;
    const totalLineCount = graphProjects.length;
    const worstLine = lossByLineData.length > 0 ? lossByLineData[0] : null;
    const secondWorstLine = lossByLineData.length > 1 ? lossByLineData[1] : null;

    return {
      totalValueCreated,
      totalProductionLoss,
      totalActualLoss,
      valueAddedCost,
      noValueAddedCost,
      wasteCost,
      nettoContribution,
      lossPercentage,
      lossByLineData,
      topLossDrivers,
      avgLossPerDay,
      daySpan,
      weeklyTotalLoss,
      yearlyTotalLoss,
      lossLineCount,
      totalLineCount,
      worstLine,
      secondWorstLine,
    };
  }, [allMeasurements, graphProjects, hasProjection]);

  // ─── Helpers ────────────────────────────────────────────────────

  const formatCurrency = (value) => {
    return `${currencySymbol} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDiff = (actual, projected) => {
    const diff = projected - actual;
    if (Math.abs(diff) < 0.01) return null;
    const sign = diff > 0 ? "+" : "";
    return `${sign}${currencySymbol} ${diff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentDiff = (actual, projected) => {
    if (actual === 0) return null;
    const diff = ((projected - actual) / Math.abs(actual)) * 100;
    if (Math.abs(diff) < 0.1) return null;
    const sign = diff > 0 ? "+" : "";
    return `${sign}${diff.toFixed(1)}%`;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}min`;
  };

  const formatCurrencyShort = (value) => {
    return `${currencySymbol}${Math.round(value).toLocaleString()}`;
  };

  const roundDown = (value) => {
    if (value <= 0) return 0;
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    return Math.floor(value / magnitude) * magnitude;
  };

  const formatCurrencyRounded = (value) => {
    return `${currencySymbol}${roundDown(value).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // ─── Empty state ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className={classes.spinner} />
      </div>
    );
  }

  if (!insights) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateCard}>
          <p className={classes.emptyStateCard__title}>No data</p>
          <p className={classes.emptyStateCard__subtitle}>There are no measurements to generate insights</p>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className={classes.insightsContainer}>
        {/* Projection banner */}
        {hasProjection && projectedInsights && (
          <div className={classes.projectionBanner}>
            <TrendingUpDown size={16} />
            <p className={classes.projectionBanner__text}>Projection active — projected values are shown alongside actual data</p>
          </div>
        )}

        {/* KPI Cards */}
        <div className={classes.kpiRow}>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: "#28A745" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>Total value created</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.totalValueCreated)}</p>
                {hasProjection && projectedInsights && formatDiff(insights.totalValueCreated, projectedInsights.totalValueCreated) && (
                  <p className={classes.kpiCard__projected}>
                    <TrendingUpDown size={12} />
                    {formatCurrency(projectedInsights.totalValueCreated)}{" "}
                    <span className={projectedInsights.totalValueCreated >= insights.totalValueCreated ? classes.diffPositive : classes.diffNegative}>
                      ({formatDiff(insights.totalValueCreated, projectedInsights.totalValueCreated)})
                    </span>
                  </p>
                )}
            </div>
            </div>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: "#DC3545" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>Total production cost</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.totalProductionLoss)}</p>
                {hasProjection && projectedInsights && formatDiff(insights.totalProductionLoss, projectedInsights.totalProductionLoss) && (
                  <p className={classes.kpiCard__projected}>
                    <TrendingUpDown size={12} />
                    {formatCurrency(projectedInsights.totalProductionLoss)}{" "}
                    <span className={projectedInsights.totalProductionLoss <= insights.totalProductionLoss ? classes.diffPositive : classes.diffNegative}>
                      ({formatDiff(insights.totalProductionLoss, projectedInsights.totalProductionLoss)})
                    </span>
                  </p>
                )}
            </div>
            {(insights.valueAddedCost > 0 || insights.noValueAddedCost > 0 || insights.wasteCost > 0) && (
              <div className={classes.kpiCard__breakdown}>
                {[
                  { label: "Value added", value: insights.valueAddedCost, color: "#28A745" },
                  { label: "No value added", value: insights.noValueAddedCost, color: "#F0AD4E" },
                  { label: "Waste", value: insights.wasteCost, color: "#DC3545" },
                ].map((row) => (
                  <div key={row.label} className={classes.kpiCard__breakdownItem}>
                    <span className={classes.kpiCard__breakdownDot} style={{ backgroundColor: row.color }} />
                    <div className={classes.kpiCard__breakdownText}>
                      <p className={classes.kpiCard__breakdownLabel}>{row.label}</p>
                      <p className={classes.kpiCard__breakdownValue}>
                        {formatCurrency(row.value)}
                        {insights.totalProductionLoss > 0 && (
                          <span className={classes.kpiCard__breakdownPct}>
                            {" "}({((row.value / insights.totalProductionLoss) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: insights.nettoContribution >= 0 ? "#28A745" : "#DC3545" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>Net contribution</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.nettoContribution)}</p>
                {hasProjection && projectedInsights && formatDiff(insights.nettoContribution, projectedInsights.nettoContribution) && (
                  <p className={classes.kpiCard__projected}>
                    <TrendingUpDown size={12} />
                    {formatCurrency(projectedInsights.nettoContribution)}{" "}
                    <span className={projectedInsights.nettoContribution >= insights.nettoContribution ? classes.diffPositive : classes.diffNegative}>
                      ({formatDiff(insights.nettoContribution, projectedInsights.nettoContribution)})
                    </span>
                  </p>
                )}
            </div>
            </div>
        </div>
        {/* KPI summary */}
        {insights.totalActualLoss > 0 ? (
          <div className={classes.kpiSummaryCard}>
              <p className={classes.kpiSummaryCard__text}>
                  <span className={classes.kpiSummaryCard__highlight}>{insights.lossPercentage}%</span> of potential value is lost due to inefficiencies
                  {hasProjection && projectedInsights && projectedInsights.lossPercentage !== insights.lossPercentage && (
                    <span className={classes.projectedInline}>
                      {" "}<ArrowRight size={14} className={classes.projectedArrowIcon} /> projected: <span className={classes.kpiSummaryCard__highlight}>{projectedInsights.lossPercentage}%</span>
                    </span>
                  )}
              </p>
          </div>
        ) : (
          <div className={classes.noLossCard}>
            <CheckCircle2 size={28} className={classes.noLossCard__icon} />
            <div>
              <p className={classes.noLossCard__title}>No production loss detected</p>
              <p className={classes.noLossCard__subtitle}>
                All registered production cost is value adding. There is nothing to improve in this timeframe.
              </p>
            </div>
          </div>
        )}

      {insights.totalActualLoss > 0 && (<>

      {/* Charts row */}
      <div className={classes.chartsRow}>
        {/* Loss by line */}
        <div className={classes.chartCard}>
          <p className={classes.chartCard__title}>Loss by line</p>
          {insights.lossByLineData.length > 0 ? (
            <table className={classes.lossTable}>
              <thead>
                <tr>
                  <th className={classes.lossTable__th}>Line</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>Time lost</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>Loss / week</th>
                  {hasProjection && projectedInsights && <th className={`${classes.lossTable__th} ${classes.lossTable__thRight} ${classes.lossTable__thProjected}`}>Projected</th>}
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>Yearly impact</th>
                  {hasProjection && projectedInsights && <th className={`${classes.lossTable__th} ${classes.lossTable__thRight} ${classes.lossTable__thProjected}`}>Projected</th>}
                </tr>
              </thead>
              <tbody>
                {insights.lossByLineData.map((line, idx) => {
                  const projLine = hasProjection && projectedInsights ? projectedInsights.lossByLineData.find((pl) => pl.name === line.name) : null;
                  return (
                    <tr key={idx} className={classes.lossTable__row}>
                      <td className={classes.lossTable__td}>{line.name}</td>
                      <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatDuration(line.duration)}</td>
                      <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatCurrency(line.lossPerWeek)}</td>
                      {hasProjection && projectedInsights && (
                        <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight} ${classes.lossTable__tdProjected}`}>
                          {projLine ? formatCurrency(projLine.lossPerWeek) : "—"}
                          {projLine && formatPercentDiff(line.lossPerWeek, projLine.lossPerWeek) && (
                            <span className={`${classes.lossTable__diff} ${projLine.lossPerWeek <= line.lossPerWeek ? classes.diffPositive : classes.diffNegative}`}>
                              {" "}{formatPercentDiff(line.lossPerWeek, projLine.lossPerWeek)}
                            </span>
                          )}
                        </td>
                      )}
                      <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatCurrency(line.yearlyImpact)}</td>
                      {hasProjection && projectedInsights && (
                        <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight} ${classes.lossTable__tdProjected}`}>
                          {projLine ? formatCurrency(projLine.yearlyImpact) : "—"}
                          {projLine && formatPercentDiff(line.yearlyImpact, projLine.yearlyImpact) && (
                            <span className={`${classes.lossTable__diff} ${projLine.yearlyImpact <= line.yearlyImpact ? classes.diffPositive : classes.diffNegative}`}>
                              {" "}{formatPercentDiff(line.yearlyImpact, projLine.yearlyImpact)}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className={classes.noDataText}>No loss data available</p>
          )}
        </div>

        {/* Top loss drivers */}
        <div className={classes.chartCard}>
          <p className={classes.chartCard__title}>Top loss drivers</p>
          {insights.topLossDrivers.length > 0 ? (
            (() => {
              const chartData = hasProjection && projectedInsights
                ? insights.topLossDrivers.map((d) => {
                    const projDriver = projectedInsights.topLossDrivers.find((pd) => pd.reason === d.reason);
                    return { ...d, projectedValue: projDriver?.value || 0 };
                  })
                : insights.topLossDrivers;
              return (
                <Chart id="topLossDriversChart" dataSource={chartData} rotated={true} customizePoint={(pointInfo) => {
                    const vt = pointInfo.data?.valueTypeId;
                    if (vt === valueTypeCodes.W) return { color: "#DC3545" };
                    if (vt === valueTypeCodes.NVA) return { color: "#F0AD4E" };
                    return {};
                  }}>
                  <CommonSeriesSettings argumentField="reason" type="bar" barWidth={hasProjection && projectedInsights ? 16 : 32} />
                  <Series valueField="value" name="Actual" color="var(--color-primary)" />
                  {hasProjection && projectedInsights && <Series valueField="projectedValue" name="Projected" color="#8B6D47" />}
                  <ArgumentAxis>
                    <Label visible={true} />
                  </ArgumentAxis>
                  <ValueAxis>
                    <Label format={{ type: "fixedPoint", precision: 0 }} />
                  </ValueAxis>
                  <Legend visible={hasProjection && projectedInsights} />
                  <Tooltip
                    enabled={true}
                    customizeTooltip={(arg) => {
                      const driver = insights.topLossDrivers.find((d) => d.reason === arg.argument);
                      if (arg.seriesName === "Projected") {
                        const projDriver = projectedInsights?.topLossDrivers.find((d) => d.reason === arg.argument);
                        return {
                          text: `${arg.argument} (Projected)\n${formatCurrency(arg.value)} (${projDriver?.percentage || 0}%)`,
                        };
                      }
                      return {
                        text: `${arg.argument}\n${formatCurrency(arg.value)} (${driver?.percentage || 0}%)\n${formatDuration(driver?.duration || 0)}`,
                      };
                    }}
                  />
                </Chart>
              );
            })()
          ) : (
            <p className={classes.noDataText}>No loss drivers available</p>
          )}
        </div>
      </div>

      {/* Key insights */}
      <div className={classes.sentencesCard}>
        <p className={classes.sentencesCard__title}>Key insights</p>
        <div className={classes.sentencesList}>
          {insights.topLossDrivers.length > 0 && (
            <p className={classes.sentence}>
              <span className={classes.sentence__highlight}>{insights.topLossDrivers[insights.topLossDrivers.length - 1].reason}</span> accounts for{" "}
              <span className={classes.sentence__highlight}>{insights.topLossDrivers[insights.topLossDrivers.length - 1].percentage}%</span> of total production loss
              {hasProjection && projectedInsights && projectedInsights.topLossDrivers.length > 0 && (
                <span className={classes.projectedInline}> <ArrowRight size={14} className={classes.projectedArrowIcon} /> projected: <span className={classes.sentence__highlight}>{projectedInsights.topLossDrivers[projectedInsights.topLossDrivers.length - 1].percentage}%</span></span>
              )}
            </p>
          )}
          <p className={classes.sentence}>
            Average production loss is <span className={classes.sentence__highlight}>{formatCurrency(insights.avgLossPerDay)}</span> per day
            {hasProjection && projectedInsights && formatDiff(insights.avgLossPerDay, projectedInsights.avgLossPerDay) && (
              <span className={classes.projectedInline}> <ArrowRight size={14} className={classes.projectedArrowIcon} /> projected: <span className={classes.sentence__highlight}>{formatCurrency(projectedInsights.avgLossPerDay)}</span></span>
            )}
          </p>
          <p className={classes.sentence}>
            Weekly production loss amounts to <span className={classes.sentence__highlight}>{formatCurrency(insights.weeklyTotalLoss)}</span>
            {hasProjection && projectedInsights && formatDiff(insights.weeklyTotalLoss, projectedInsights.weeklyTotalLoss) && (
              <span className={classes.projectedInline}> <ArrowRight size={14} className={classes.projectedArrowIcon} /> projected: <span className={classes.sentence__highlight}>{formatCurrency(projectedInsights.weeklyTotalLoss)}</span></span>
            )}
          </p>
          <p className={classes.sentence}>
            Annual impact exceeds <span className={classes.sentence__highlight}>{formatCurrencyRounded(insights.yearlyTotalLoss)}</span>
            {hasProjection && projectedInsights && formatDiff(insights.yearlyTotalLoss, projectedInsights.yearlyTotalLoss) && (
              <span className={classes.projectedInline}> <ArrowRight size={14} className={classes.projectedArrowIcon} /> projected: <span className={classes.sentence__highlight}>{formatCurrencyRounded(projectedInsights.yearlyTotalLoss)}</span></span>
            )}
          </p>
          <p className={classes.sentence}>
            Loss is spread across <span className={classes.sentence__highlight}>{insights.lossLineCount} out of {insights.totalLineCount}</span> production lines
          </p>
          {insights.worstLine && (
            <p className={classes.sentence}>
              <span className={classes.sentence__highlight}>{insights.worstLine.name}</span> is the worst performing line (<span className={classes.sentence__highlight}>{formatCurrency(insights.worstLine.lossPerWeek)}</span> loss per week)
              {hasProjection && projectedInsights && projectedInsights.worstLine && (
                <span className={classes.projectedInline}> <ArrowRight size={14} className={classes.projectedArrowIcon} /> projected: <span className={classes.sentence__highlight}>{formatCurrency(projectedInsights.worstLine.lossPerWeek)}</span>/week</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Improvement potential */}
      {insights.worstLine && (
        <div className={classes.sentencesCard}>
          <p className={classes.sentencesCard__title}>Improvement potential</p>
          <div className={classes.sentencesList}>
            <div className={classes.improvementBlock}>
              <p className={classes.sentence}>
                Reducing losses on <span className={classes.sentence__highlight}>{insights.worstLine.name}</span> by 10%:
              </p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> <span className={classes.sentence__highlight}>{formatCurrencyShort(insights.worstLine.lossPerWeek * 0.1)}</span> savings per week</p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> <span className={classes.sentence__highlight}>{formatCurrencyShort(insights.worstLine.yearlyImpact * 0.1)}</span> per year</p>
            </div>
            {insights.secondWorstLine && (
              <div className={classes.improvementBlock}>
                <p className={classes.sentence}>
                  Reducing losses on top 2 lines by 10%:
                </p>
                <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> <span className={classes.sentence__highlight}>{formatCurrencyRounded((insights.worstLine.yearlyImpact + insights.secondWorstLine.yearlyImpact) * 0.1)}+</span> annual improvement potential</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recommended starting point */}
      {insights.worstLine && (
        <div className={classes.sentencesCard}>
          <p className={classes.sentencesCard__title}>Recommended starting point</p>
          <div className={classes.sentencesList}>
            <div className={classes.improvementBlock}>
              <p className={classes.sentence}>
                Focus on <span className={classes.sentence__highlight}>{insights.worstLine.name}</span>{insights.secondWorstLine ? <> and <span className={classes.sentence__highlight}>{insights.secondWorstLine.name}</span></> : ""}:
              </p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> Highest financial impact</p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> Fastest ROI potential</p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> Immediate visibility on improvement actions</p>
            </div>
          </div>
        </div>
      )}
      </>)}

      {/* Analysis disclaimer */}
      <div className={classes.disclaimerCard}>
        <p className={classes.disclaimerCard__title}>Analysis based on observed production data</p>
        <div className={classes.disclaimerList}>
          <p className={classes.disclaimerItem}>Timeframe: {formatDate(insights.rangeStart)} – {formatDate(insights.rangeEnd)} ({insights.daySpan} {insights.daySpan === 1 ? "day" : "days"})</p>
          <p className={classes.disclaimerItem}>Scope: {insights.totalLineCount} production line{insights.totalLineCount !== 1 ? "s" : ""}</p>
          <p className={classes.disclaimerItem}>Based on real production events and loss registration</p>
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  insightsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "0px 0px 16px 0",
    paddingBottom: "0px",
    width: "100%",
  },

  // ─── KPI cards ───
  kpiRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  kpiCard: {
    flex: 1,
    minWidth: "200px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
  },

  kpiCard__indicator: {
    width: "4px",
    flexShrink: 0,
  },

  kpiCard__content: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "20px 24px",
  },

  kpiCard__wide: {
    flex: 1,
  },

  kpiCard__breakdown: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 16px",
    marginLeft: "auto",
    borderLeft: "1px solid #EDEDED",
    minWidth: 0,
  },

  kpiCard__breakdownItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  kpiCard__breakdownDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  kpiCard__breakdownText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },

  kpiCard__breakdownLabel: {
    margin: 0,
    fontSize: "10px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  kpiCard__breakdownValue: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  kpiCard__breakdownPct: {
    fontSize: "11px",
    fontWeight: 400,
    color: "#6C6C6C",
  },

  kpiCard__label: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  kpiCard__value: {
    margin: 0,
    fontSize: "28px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  kpiSummaryCard: {
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "#FFF9ED",
    border: "1px solid #FEF1D6",
  },

  kpiSummaryCard__text: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    margin: 0,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  kpiSummaryCard__highlight: {
    fontWeight: 700,
    fontSize: "18px",
  },

  noLossCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#F1FBF4",
    border: "1px solid #C8EAD3",
  },

  noLossCard__icon: {
    color: "#28A745",
    flexShrink: 0,
  },

  noLossCard__title: {
    margin: 0,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  noLossCard__subtitle: {
    margin: "4px 0 0 0",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#3A6F4A",
    lineHeight: "1.5",
  },

  // ─── Charts ───
  chartsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  chartCard: {
    flex: 1,
    minWidth: "300px",
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
  },

  chartCard__title: {
    margin: "0 0 12px 0",
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 'normal',
    color: "#0C0E29",
  },

  lossTable: {
    width: "100%",
    borderCollapse: "collapse",
  },

  lossTable__th: {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #EDEDED",
  },

  lossTable__thRight: {
    textAlign: "right",
  },

  lossTable__row: {
    "&:hover": {
      backgroundColor: "#F9F9F9",
    },
  },

  lossTable__td: {
    padding: "10px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    borderBottom: "1px solid #F0F0F0",
  },

  lossTable__tdRight: {
    textAlign: "right",
    fontWeight: 600,
  },

  noDataText: {
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textAlign: "center",
    padding: "24px 0",
  },

  // ─── Sentences ───
  sentencesCard: {
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
  },

  sentencesCard__title: {
    margin: "0 0 16px 0",
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 'normal',
    color: "#0C0E29",
  },

  sentencesList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  sentence: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    lineHeight: "1.6",
    padding: "8px 12px",
    borderRadius: "6px",
    backgroundColor: "#F9F9F9",
  },

  sentence__highlight: {
    fontWeight: 700,
    color: "var(--color-primary)",
  },

  // ─── States ───
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },

  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(12, 14, 41, 0.1)",
    borderTop: "3px solid rgba(12, 14, 41, 0.7)",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },

  emptyStateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },

  emptyStateCard: {
    padding: "24px",
    textAlign: "center",
  },

  emptyStateCard__title: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#0C0E29",
  },

  emptyStateCard__subtitle: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
  },

  // ─── Improvement & recommendation ───
  improvementBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  improvementArrow: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    lineHeight: "1.6",
    paddingLeft: "24px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  improvementArrowIcon: {
    flexShrink: 0,
    color: "var(--color-primary)",
  },

  // ─── Disclaimer ───
  disclaimerCard: {
    padding: "16px 24px",
    borderRadius: "8px",
    backgroundColor: "#F5F5F5",
    border: "1px solid #EDEDED",
  },

  disclaimerCard__title: {
    margin: "0 0 12px 0",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#6C6C6C",
  },

  disclaimerList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  disclaimerItem: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    lineHeight: "1.5",
  },

  // ─── Projection styles ───
  projectionBanner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    borderRadius: "8px",
    backgroundColor: "#F5F0EA",
    border: "1px solid #E0D4C6",
    color: "#8B6D47",
  },

  projectionBanner__text: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 500,
    color: "#8B6D47",
  },

  kpiCard__projected: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#8B6D47",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "4px",
  },

  diffPositive: {
    color: "#28A745",
    fontWeight: 600,
    fontSize: "12px",
  },

  diffNegative: {
    color: "#DC3545",
    fontWeight: 600,
    fontSize: "12px",
  },

  projectedInline: {
    color: "#8B6D47",
    fontSize: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginLeft: "4px",
  },

  projectedArrowIcon: {
    verticalAlign: "middle",
    color: "#8B6D47",
    flexShrink: 0,
  },

  lossTable__thProjected: {
    color: "#8B6D47",
  },

  lossTable__tdProjected: {
    color: "#8B6D47",
  },

  lossTable__diff: {
    fontSize: "11px",
    fontWeight: 600,
    marginLeft: "4px",
  },
}));
